import React from "react";
import { Link } from "react-router-dom";
import FinlyLogo from "@/components/FinlyLogo";

export default function Terms() {
  return (
    <div className="min-h-screen bg-[#FBF7F0] text-[#0F172A] font-body antialiased" data-testid="terms-page">
      <nav className="sticky top-0 z-40 bg-white/85 backdrop-blur-md border-b border-[#E2E8F0]">
        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="terms-nav-logo">
            <FinlyLogo size={32} rounded="22%" />
            <span className="font-display font-semibold text-base">Finly+</span>
          </Link>
          <Link
            to="/"
            className="text-sm text-[#5C6B3A] hover:text-[#0F172A] transition-colors"
            data-testid="terms-back-home"
          >
            ← Volver al inicio
          </Link>
        </div>
      </nav>

      <article className="max-w-3xl mx-auto px-6 py-14">
        <header className="mb-10">
          <p className="text-xs uppercase tracking-[0.22em] text-[#8A9A5B] mb-3">Legal</p>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">Términos y Condiciones</h1>
          <p className="mt-3 text-sm text-[#64748B]">Última actualización: 1 de junio de 2026</p>
        </header>

        <section className="prose-finly space-y-8 text-[15px] leading-relaxed text-[#1F2937]">
          <div>
            <h2 className="text-xl font-semibold mb-2">1. Aceptación</h2>
            <p>
              Al crear una cuenta o usar Finly+ (el "Servicio"), accesible en <strong>finly.uno</strong>,
              aceptas estos Términos. Si no estás de acuerdo, no uses el Servicio. Finly+ es
              operado por Jabes Avalúos ("nosotros").
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold mb-2">2. Descripción del Servicio</h2>
            <p>
              Finly+ es una aplicación de finanzas personales que permite registrar ingresos y gastos,
              clasificarlos en categorías, ver resúmenes mensuales, y opcionalmente capturar
              transacciones por voz o por foto (OCR). El Servicio incluye un plan gratuito (Free)
              y un plan de suscripción (Premium) con funciones adicionales.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold mb-2">3. Cuenta y seguridad</h2>
            <p>
              Eres responsable de mantener la confidencialidad de tu contraseña y de toda actividad
              que ocurra en tu cuenta. Debes notificarnos de inmediato cualquier uso no autorizado.
              Nos reservamos el derecho de suspender cuentas con actividad sospechosa o fraudulenta.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold mb-2">4. Prueba gratuita y suscripción Premium</h2>
            <ul className="list-disc pl-6 space-y-1.5">
              <li>Toda cuenta nueva incluye <strong>10 días de prueba gratuita</strong> de Premium.</li>
              <li>Al concluir el periodo de prueba, las funciones Premium quedan bloqueadas hasta que actives una suscripción.</li>
              <li>Las suscripciones se procesan mediante Stripe (<a className="text-[#5C6B3A] underline" href="https://stripe.com" target="_blank" rel="noreferrer">stripe.com</a>). Los precios actuales son USD $3 / mes o USD $30 / año.</li>
              <li>Puedes cancelar en cualquier momento; el acceso Premium se mantiene hasta el final del periodo pagado.</li>
              <li>No emitimos reembolsos por periodos parciales salvo que la ley lo exija.</li>
            </ul>
          </div>

          <div>
            <h2 className="text-xl font-semibold mb-2">5. Contenido del usuario</h2>
            <p>
              Tú conservas todos los derechos sobre la información financiera que registras
              (transacciones, notas, fotografías de tickets, audios). Nos otorgas una licencia
              limitada para almacenar y procesar esa información con el único fin de prestarte el
              Servicio. No vendemos tus datos a terceros.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold mb-2">6. Uso aceptable</h2>
            <p>No puedes: (a) usar el Servicio para fines ilegales o fraudulentos, (b) intentar
            acceder a cuentas de otros usuarios, (c) ingeniar de manera inversa, descompilar o
            extraer el código del Servicio, (d) sobrecargar nuestra infraestructura con tráfico
            automatizado.</p>
          </div>

          <div>
            <h2 className="text-xl font-semibold mb-2">7. Servicios de terceros</h2>
            <p>El Servicio depende de proveedores externos para funcionar: Stripe (pagos), Resend
            (correo), OpenAI / Google Gemini (procesamiento por AI de voz y OCR), MongoDB Atlas
            (base de datos). El uso del Servicio implica aceptación de los términos relevantes
            de dichos proveedores.</p>
          </div>

          <div>
            <h2 className="text-xl font-semibold mb-2">8. Sin asesoría financiera</h2>
            <p>
              Las recomendaciones, semáforos y predicciones del Servicio son
              <strong> informativos</strong> y se generan a partir de tus propios datos y reglas
              automatizadas. No constituyen asesoría financiera, fiscal ni de inversión. Consulta
              con un profesional certificado antes de tomar decisiones importantes.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold mb-2">9. Disponibilidad y cambios</h2>
            <p>
              Hacemos lo mejor para mantener el Servicio operativo 24/7, pero no garantizamos
              disponibilidad ininterrumpida. Podemos modificar, suspender o discontinuar funciones
              en cualquier momento, notificando con razonable antelación los cambios materiales.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold mb-2">10. Terminación</h2>
            <p>
              Puedes cerrar tu cuenta en cualquier momento desde la configuración. Podemos
              suspender o cancelar tu acceso si incumples estos Términos o si lo exige la ley.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold mb-2">11. Limitación de responsabilidad</h2>
            <p>
              En la máxima medida permitida por la ley, Finly+ y Jabes Avalúos no son responsables
              de daños indirectos, incidentales, especiales o consecuenciales derivados del uso o
              imposibilidad de uso del Servicio.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold mb-2">12. Ley aplicable</h2>
            <p>
              Estos Términos se rigen por las leyes de los Estados Unidos Mexicanos. Cualquier
              controversia se resolverá en los tribunales competentes de la Ciudad de México,
              salvo derecho diverso de la legislación de protección al consumidor.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold mb-2">13. Contacto</h2>
            <p>
              Si tienes dudas sobre estos Términos, escríbenos a{" "}
              <a className="text-[#5C6B3A] underline" href="mailto:hola@finly.uno">hola@finly.uno</a>.
            </p>
          </div>
        </section>

        <footer className="mt-14 pt-8 border-t border-[#E2E8F0] text-xs text-[#94A3B8] flex items-center justify-between">
          <span>© 2026 Finly+ · Jabes Avalúos</span>
          <Link to="/privacidad" className="hover:text-[#0F172A] transition-colors" data-testid="terms-link-privacy">
            Política de Privacidad →
          </Link>
        </footer>
      </article>
    </div>
  );
}
